import * as React from "react"
import * as LabelPrimitive from "@radix-ui/react-label"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

const labelVariants = cva(
  "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
)

/**
 * Label — Radix-based label primitive.
 *
 * New props (WCAG 2.1 Phase 2):
 *   • `required` — renders a red asterisk after the label text and exposes
 *     it to assistive tech via `aria-hidden` (the visual cue), so the
 *     associated control should set `aria-required="true"` to be announced.
 *   • `optionalHint` — renders a muted "(optional)" suffix when not required.
 *     Useful in long forms where the absence of `*` is otherwise ambiguous.
 */
const Label = React.forwardRef(({ className, required, optionalHint, children, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn(labelVariants(), className)} {...props}>
    {children}
    {required && (
      <span
        aria-hidden="true"
        className="ml-0.5 text-red-600 font-bold"
        title="Required field"
      >
        *
      </span>
    )}
    {!required && optionalHint && (
      <span
        aria-hidden="true"
        className="ml-1 text-xs font-normal text-slate-500"
      >
        (optional)
      </span>
    )}
  </LabelPrimitive.Root>
))
Label.displayName = LabelPrimitive.Root.displayName

/**
 * FormError — inline error message paired with a form control.
 *
 * Usage:
 *   <Input id="email" aria-invalid={!!err} aria-describedby={err ? 'email-err' : undefined} />
 *   <FormError id="email-err">{err}</FormError>
 *
 * The element is announced by screen-readers via `role="alert"` /
 * `aria-live="assertive"` as soon as it appears.
 */
const FormError = React.forwardRef(({ id, className, children, ...props }, ref) => {
  if (!children) return null;
  return (
    <p
      ref={ref}
      id={id}
      role="alert"
      aria-live="assertive"
      className={cn("text-sm text-red-700 mt-1 font-medium", className)}
      {...props}
    >
      {children}
    </p>
  );
});
FormError.displayName = "FormError";

/**
 * FormHelp — a description paragraph linked to a control via aria-describedby.
 * Use this for hint text (e.g. "Min 8 characters, 1 number").
 */
const FormHelp = React.forwardRef(({ id, className, children, ...props }, ref) => {
  if (!children) return null;
  return (
    <p
      ref={ref}
      id={id}
      className={cn("text-xs text-slate-600 mt-1", className)}
      {...props}
    >
      {children}
    </p>
  );
});
FormHelp.displayName = "FormHelp";

export { Label, FormError, FormHelp }
